import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Bed,
  Bath,
  Ruler,
  ArrowRight,
  PhoneCall,
  Heart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Property } from "@/hooks/useProperties";
import { useFavorites } from "@/hooks/useFavorites";

const globalCache: Record<
  string,
  { images: string[]; detailedTitle: string }
> = {};

interface PropertyCardProps {
  property: Property;
  onPreview: (property: Property) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onPreview,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  const [extraData, setExtraData] = useState({
    images: property.foto ? [property.foto] : [],
    detailedTitle: property.poblacion || "Property",
    loading: true,
  });

  useEffect(() => {
    const fetchDetails = async () => {
      const langMap: Record<string, string> = {
        es: "1",
        en: "2",
        fr: "3",
      };

      const shortLang = (i18n.language || "es").split("-")[0];
      const langId = langMap[shortLang] || "1";

      const cacheKey = `${property.cod_ofer}_${langId}`;

      if (globalCache[cacheKey]) {
        setExtraData({ ...globalCache[cacheKey], loading: false });
        return;
      }

      try {
        const res = await fetch(
          `/api/inmovilla/api_v1.php?action=detail&id=${property.cod_ofer}&lang=${langId}`,
          { credentials: "same-origin" }
        );

        const result = await res.json();

        const ficha = result.ficha || {};
        const desc =
          result.descripciones?.[property.cod_ofer]?.[langId] || {};
        const fotosObj = result.fotos?.[property.cod_ofer] || {};

        const gallery = Object.values(fotosObj).filter(
          (url): url is string =>
            typeof url === "string" && url.includes("http")
        );

        const finalData = {
          images:
            gallery.length > 0
              ? gallery
              : property.foto
              ? [property.foto]
              : [],
          detailedTitle:
            desc.titulo ||
            ficha.titulo ||
            property.poblacion ||
            "Property",
        };

        globalCache[cacheKey] = finalData;
        setExtraData({ ...finalData, loading: false });
      } catch (err) {
        console.error(err);
        setExtraData((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchDetails();
  }, [property.cod_ofer, i18n.language, property.poblacion, property.foto]);

  const isRental = Number(property.keyacci) === 2;
  const displayPrice = isRental ? property.precioalq : property.precioinmo;
  const hasImages = extraData.images.length > 0;

  return (
    <article
      className="group bg-white flex flex-col min-h-[600px] h-full relative transition-all duration-700 border border-stone-100/50 hover:border-stone-200 hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] overflow-hidden rounded-sm"
      onMouseEnter={() => {
        setIsHovered(true);
        // Preload the property details page component chunk
        import("@/pages/properties/[id]");
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* IMAGE */}
      <div className="relative h-[380px] w-full overflow-hidden bg-[#f1ece1] shrink-0">
        <AnimatePresence mode="wait">
          {hasImages ? (
            <motion.img
              key={extraData.images[index]}
              src={extraData.images[index]}
              alt={extraData.detailedTitle}
              width={800}
              height={600}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: isHovered ? 1.08 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-stone-50">
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply bg-[url('/assets/textures/grain.png')]" />
              <div className="w-16 h-16 rounded-full border border-stone-200 flex items-center justify-center mb-4 bg-white shadow-sm">
                <span className="font-playfair italic text-xl text-stone-300">D</span>
              </div>
              <span className="font-oswald text-[9px] uppercase tracking-[0.3em] text-stone-400 relative z-10">
                DEM PROPERTIES
              </span>
            </div>
          )}
        </AnimatePresence>

        {/* SLIDER */}
        {extraData.images.length > 1 && isHovered && (
          <div className="absolute inset-0 flex items-center justify-between px-4 z-20">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIndex(
                  (prev) =>
                    (prev - 1 + extraData.images.length) %
                    extraData.images.length
                );
              }}
              className="w-10 h-10 bg-black/20 backdrop-blur text-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition"
              aria-label={t("property.actions.previousImage", "Previous image")}
            >
              <ChevronLeft size={18} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIndex(
                  (prev) => (prev + 1) % extraData.images.length
                );
              }}
              className="w-10 h-10 bg-black/20 backdrop-blur text-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition"
              aria-label={t("property.actions.nextImage", "Next image")}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        <div className="absolute bottom-6 left-6 z-20">
          <span className="bg-stone-900 text-white px-4 py-1.5 font-oswald text-[9px] uppercase tracking-[0.2em] shadow-md">
            {isRental
              ? t("property.operation.rent")
              : t("property.operation.sale")}
          </span>
        </div>

        <div className="absolute top-6 right-6 z-30 flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(property);
            }}
            className={`w-10 h-10 flex items-center justify-center transition shadow-md rounded-sm ${
              isFavorite(property.cod_ofer) 
                ? "bg-[#a5694f] text-white" 
                : "bg-white/95 text-stone-900 hover:bg-[#a5694f]/10 hover:text-[#a5694f]"
            }`}
            aria-label="Toggle Favorite"
          >
            <Heart size={16} fill={isFavorite(property.cod_ofer) ? "currentColor" : "none"} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(property);
            }}
            className="w-10 h-10 bg-white/95 flex items-center justify-center text-stone-900 hover:bg-[#a5694f] hover:text-white transition shadow-md rounded-sm"
            aria-label={t("property.actions.viewFullscreen", "View fullscreen")}
          >
            <Maximize2 size={16} />
          </button>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex flex-col flex-1 p-6 md:p-8">
        <div className="mb-4">
          <div className="flex justify-between items-start gap-4 mb-3">
            <h3 className="font-oswald font-light text-xl leading-[1.35] uppercase tracking-wide text-stone-800 line-clamp-2 flex-1 min-h-[54px]">
              {extraData.detailedTitle}
            </h3>

            <span className="font-oswald text-[9px] text-stone-300 uppercase tracking-widest mt-1.5 shrink-0">
              {t("property.ref")} {property.ref}
            </span>
          </div>

          <p className="font-playfair italic text-stone-400 text-sm flex items-center gap-2 uppercase tracking-wider truncate">
            <span className="w-4 h-[1px] bg-[#a5694f]/40" />
            {property.ciudad || property.poblacion}
          </p>
        </div>

        <div className="flex justify-between border-y border-stone-100 py-6 mt-auto">
          <SpecItem
            icon={<Bed size={16} />}
            value={property.total_hab}
            label={t("property.specs.beds")}
          />
          <SpecItem
            icon={<Bath size={16} />}
            value={property.banyos}
            label={t("property.specs.baths")}
          />
          <SpecItem
            icon={<Ruler size={16} />}
            value={property.m_cons}
            label={t("property.specs.size")}
          />
        </div>

        <div className="flex justify-between items-end pt-6">
          <div className="flex flex-col gap-1">
            <span className="text-[8px] uppercase tracking-[0.2em] text-stone-400 font-oswald">
              {t("property.price.label")}
            </span>

            {Number(displayPrice) === 0 ? (
              <div className="flex items-center gap-2 text-[#a5694f] font-oswald text-[10px] uppercase tracking-widest">
                <PhoneCall size={12} />
                {t("property.price.onRequest")}
              </div>
            ) : (
              <p className="text-2xl font-playfair italic text-stone-900 leading-none">
                {Number(displayPrice).toLocaleString()} €
                {isRental && (
                  <span className="text-[10px] ml-1 text-stone-400 font-oswald uppercase">
                    / mo.
                  </span>
                )}
              </p>
            )}
          </div>

          <button
            onClick={() =>
              navigate(`/properties/${property.cod_ofer}`)
            }
            className="flex items-center gap-3 font-oswald text-[10px] uppercase tracking-[0.3em] text-stone-900 group/btn"
          >
            <span className="relative pb-1">
              {t("property.actions.viewDetails")}
              <span className="absolute bottom-0 left-0 w-full h-[1px] bg-stone-100 group-hover/btn:bg-[#a5694f] transition" />
            </span>
            <ArrowRight
              size={14}
              className="group-hover/btn:translate-x-1 transition"
            />
          </button>
        </div>
      </div>
    </article>
  );
};

const SpecItem = ({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: any;
  label: string;
}) => (
  <div className="flex flex-col items-center flex-1">
    <div className="text-[#a5694f]/70 mb-1.5">{icon}</div>
    <span className="font-playfair text-base text-stone-800 font-medium">
      {value && value !== "0" ? value : "-"}
    </span>
    <span className="font-oswald text-[7px] uppercase tracking-[0.15em] text-stone-300 mt-1">
      {label}
    </span>
  </div>
);

export default PropertyCard;
